import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import i18n from '../i18n';
import { Button } from './ui/Button';
import { reportFrontendError } from '../services/frontendErrorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * LOT-BLINDAGE (B3) — remontée CENTRALISÉE des erreurs captées par le boundary.
 *
 * État des lieux (vérifié) : AUCUN SDK Sentry côté frontend — pas de paquet
 * `@sentry/*` dans apps/frontend/package.json, pas d'init dans main.tsx ; seules
 * les variables `VITE_SENTRY_*` existent dans `.env.example`, sans consommateur.
 * On n'introduit donc PAS la dépendance ici (décision DSN à trancher par
 * l'architecte) : cette fonction est le POINT UNIQUE où brancher
 * `Sentry.captureException` le jour où le SDK est installé. En attendant, un
 * `console.error` STRUCTURÉ (erreur + componentStack) — visible dans la console
 * de l'iPad en recette et dans tout collecteur de logs navigateur.
 */
export function reportBoundaryError(error: Error, errorInfo: ErrorInfo | null): void {
  console.error('[ErrorBoundary] Erreur de rendu capturée', {
    message: error.message,
    name: error.name,
    stack: error.stack,
    componentStack: errorInfo?.componentStack ?? null,
    url: typeof window !== 'undefined' ? window.location.href : null,
    timestamp: new Date().toISOString(),
  });

  // LOT #146 — remontée au SERVEUR (journal + compteur) : l'erreur n'est plus
  // visible que dans la console de l'iPad. `reportFrontendError` ne jette
  // jamais, ne boucle jamais (file mémoire, dédoublonnage, repli hors ligne).
  reportFrontendError({
    kind: 'render',
    error,
    componentStack: errorInfo?.componentStack ?? null,
  });

  // Branchement Sentry (quand le SDK frontend existera) :
  // Sentry.captureException(error, {
  //   contexts: { react: { componentStack: errorInfo?.componentStack } },
  // });
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary pour capturer les erreurs React et afficher un fallback UI
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // LOT-BLINDAGE (B3) : remontée centralisée — voir `reportBoundaryError`.
    reportBoundaryError(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Si un fallback custom est fourni
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Fallback UI par défaut
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            {/* Card avec gradient border */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 rounded-2xl blur-xl"></div>
              
              <div className="relative bg-slate-900/90 backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 shadow-2xl">
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div className="bg-gradient-to-br from-red-500 to-orange-600 p-4 rounded-2xl shadow-lg shadow-red-500/20">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-white text-center mb-3">
                  Une erreur est survenue
                </h2>

                {/* Description */}
                <p className="text-gray-400 text-center mb-6">
                  Une erreur inattendue s'est produite. Nous en avons été informés et travaillons à la résoudre.
                </p>

                {/* Error details (dev only) */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <div className="mb-6 bg-slate-950/50 border border-red-500/20 rounded-xl p-4 overflow-auto max-h-48">
                    <p className="text-xs font-mono text-red-400 mb-2">
                      {this.state.error.toString()}
                    </p>
                    {this.state.errorInfo && (
                      <pre className="text-xs font-mono text-slate-500 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  {/* LOT-H3 — deux libellés codés en dur (§7) et un dégradé ROUGE→ORANGE
                      pour « Réessayer », qui ne détruit rien : le rouge y annonçait une
                      erreur, pas la nature de l'action (§1.2). Cette classe React ne peut
                      pas appeler le hook `useTranslation` : elle passe par l'instance
                      i18next partagée. */}
                  <Button
                    variant="primary"
                    icon={RefreshCw}
                    onClick={this.handleReset}
                    className="flex-1"
                  >
                    {i18n.t('shell:errorBoundary.retry')}
                  </Button>

                  <Button
                    variant="secondary"
                    icon={Home}
                    onClick={() => window.location.href = '/'}
                    className="flex-1"
                  >
                    {i18n.t('shell:errorBoundary.backHome')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook pour utiliser l'error boundary de manière programmatique
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return setError;
};
